import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../services/apiClient';
import LocationPicker from '../../components/logistica/LocationPicker.jsx';
import {
  Page, Header, Title, Controls, SearchInput, BackBtn, AddBtn,
  TableWrap, Table, Th, Td, ActionsCell, ActionBtn,
  Overlay, ModalBox, ModalHead, ModalTitle, CloseBtn, ModalBody, ModalFoot,
  FormGrp, Lbl, Inp, Row2, ErrMsg, BtnPrimary, BtnSecondary, BtnDanger,
} from '../../components/configuracion/logisticaLayoutStyles';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import ImportarModal from '../../components/importacion/ImportarModal';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';

const Textarea = styled.textarea`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem;
  min-height: 80px; box-sizing: border-box; resize: vertical;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
`;

const EMPTY = { nombre: '', contacto_nombre: '', contacto_telefono: '', contacto_correo: '', direccion: '', latitud: '', longitud: '', observaciones: '' };

export default function ConfiguracionTransportistas({ modalOnly = false, onRecordSaved = null, onRequestClose = null }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('transportistas/');
      setItems(Array.isArray(r.data) ? r.data : (r.data.results || []));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => { if (modalOnly) { abrir(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase();
    return items.filter(i =>
      i.nombre?.toLowerCase().includes(q) ||
      i.contacto_nombre?.toLowerCase().includes(q) ||
      i.contacto_telefono?.toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const [mapLocation, setMapLocation] = useState(null);

  const abrir = (item = null) => {
    setEditando(item);
    const lat = item?.latitud != null ? String(item.latitud) : '';
    const lng = item?.longitud != null ? String(item.longitud) : '';
    setForm(item
      ? { nombre: item.nombre || '', contacto_nombre: item.contacto_nombre || '', contacto_telefono: item.contacto_telefono || '', contacto_correo: item.contacto_correo || '', direccion: item.direccion || '', latitud: lat, longitud: lng, observaciones: item.observaciones || '' }
      : EMPTY);
    setMapLocation(lat && lng ? {
      address: item?.direccion || '',
      lat: parseFloat(lat), lng: parseFloat(lng),
      direccion: item?.direccion || '',
    } : null);
    setErrors({});
    setModal(true);
  };

  const handleMapLocation = (data) => {
    setMapLocation(data);
    if (!data) {
      setForm(f => ({ ...f, latitud: '', longitud: '' }));
      return;
    }
    const lat = data.lat != null && !isNaN(data.lat) ? String(data.lat) : '';
    const lng = data.lng != null && !isNaN(data.lng) ? String(data.lng) : '';
    setForm(f => ({
      ...f,
      latitud: lat,
      longitud: lng,
      direccion: data.direccion || data.address || f.direccion,
    }));
  };

  const cerrar = () => { setModal(false); setEditando(null); onRequestClose?.(); };
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        contacto_nombre: form.contacto_nombre,
        contacto_telefono: form.contacto_telefono,
        contacto_correo: form.contacto_correo,
        direccion: form.direccion,
        latitud: (() => { const v = parseFloat(form.latitud); return isNaN(v) ? null : parseFloat(v.toFixed(8)); })(),
        longitud: (() => { const v = parseFloat(form.longitud); return isNaN(v) ? null : parseFloat(v.toFixed(8)); })(),
        observaciones: form.observaciones,
      };
      let savedRecord;
      if (editando) {
        const resp = await apiClient.patch(`transportistas/${editando.id}/`, payload);
        savedRecord = resp.data;
      } else {
        const resp = await apiClient.post('transportistas/', payload);
        savedRecord = resp.data;
      }
      if (modalOnly && onRecordSaved) {
        onRecordSaved(savedRecord);
        onRequestClose?.();
      } else {
        cerrar(); cargar();
      }
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') setErrors(detail);
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    try { await apiClient.delete(`transportistas/${id}/`); setConfirmId(null); cargar(); }
    catch (e) { console.error(e); }
  };

  if (modalOnly) {
    return modal ? (
      <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
        <ModalBox $maxWidth="520px" onClick={e => e.stopPropagation()}>
          <ModalHead>
            <ModalTitle>Nuevo transportista</ModalTitle>
            <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
          </ModalHead>
          <ModalBody>
            <FormGrp>
              <Lbl>Nombre *</Lbl>
              <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre de la empresa" />
              {errors.nombre && <ErrMsg>{errors.nombre}</ErrMsg>}
            </FormGrp>
            <FormGrp>
              <Lbl>Dirección</Lbl>
              <LocationPicker key={editando?.id ?? 'nuevo'} value={mapLocation} onChange={handleMapLocation} placeholder="Buscar dirección..." />
              <Inp style={{ marginTop: 6 }} value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Dirección" />
            </FormGrp>
            <Row2>
              <FormGrp>
                <Lbl>Contacto</Lbl>
                <Inp value={form.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} placeholder="Nombre del contacto" />
              </FormGrp>
              <FormGrp>
                <Lbl>Teléfono</Lbl>
                <Inp value={form.contacto_telefono} onChange={e => set('contacto_telefono', e.target.value)} placeholder="Teléfono" />
              </FormGrp>
            </Row2>
            <FormGrp>
              <Lbl>Correo electrónico</Lbl>
              <Inp type="email" value={form.contacto_correo} onChange={e => set('contacto_correo', e.target.value)} placeholder="correo@ejemplo.com" />
            </FormGrp>
          </ModalBody>
          <ModalFoot>
            <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
            <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
          </ModalFoot>
        </ModalBox>
      </Overlay>
    ) : null;
  }

  return (
    <Page>
      <Header>
        <Title><i className="fas fa-truck" /> Transportistas</Title>
        <HelpToggleBtn
          type="button"
          $active={helpOpen}
          onClick={() => setHelpOpen(v => !v)}
        >
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            tips={[
              'Cargá acá las empresas de transporte externas que contratás para trasladar equipos.',
              'Los viajes de terceros se valorizan con la tarifa del transportista, no con la de la flota propia.',
              'Tener los datos de contacto cargados agiliza la coordinación cuando hay que despachar con urgencia.',
            ]}
            nextSteps={[
              'Seleccionar el transportista al registrar un traslado con flota externa.',
              'Consultar el informe de costos de logística para comparar el gasto entre transportistas.',
            ]}
            inlineOpen
          />
        </div>
      )}
      <Controls>
        <SearchInput placeholder="Buscar transportista..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <BackBtn onClick={() => navigate('/configuracion?seccion=logistica')}>
            <i className="fas fa-arrow-left" /> Volver
          </BackBtn>
          <BackBtn onClick={() => setShowImportModal(true)}>
            <i className="fas fa-file-import" /> Importar
          </BackBtn>
          <AddBtn onClick={() => abrir()}><i className="fas fa-plus" /> Agregar</AddBtn>
        </div>
      </Controls>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>Contacto</Th>
              <Th>Teléfono</Th>
              <Th>Email</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>}
            {!loading && filtrados.length === 0 && <tr><Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Sin registros</Td></tr>}
            {filtrados.map(item => (
              <tr key={item.id}>
                <Td>{item.nombre}</Td>
                <Td>{item.contacto_nombre || '-'}</Td>
                <Td>{item.contacto_telefono || '-'}</Td>
                <Td>{item.contacto_correo || '-'}</Td>
                <ActionsCell>
                  <ActionBtn onClick={() => abrir(item)}>Editar</ActionBtn>
                  <ActionBtn $danger onClick={() => setConfirmId(item.id)}>Eliminar</ActionBtn>
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {modal && (
        <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="560px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editando ? 'Editar transportista' : 'Nuevo transportista'}</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              <FormGrp>
                <Lbl>Nombre *</Lbl>
                <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre de la empresa transportista" />
                {errors.nombre && <ErrMsg>{errors.nombre}</ErrMsg>}
              </FormGrp>
              <FormGrp>
                <Lbl>Dirección</Lbl>
                <LocationPicker
                  key={editando?.id ?? 'nuevo'}
                  value={mapLocation}
                  onChange={handleMapLocation}
                  placeholder="Buscar dirección del transportista en el mapa..."
                />
                <Inp
                  style={{ marginTop: 6 }}
                  value={form.direccion}
                  onChange={e => set('direccion', e.target.value)}
                  placeholder="Dirección (se completa desde el mapa o escribir manualmente)"
                />
              </FormGrp>
              <Row2>
                <FormGrp>
                  <Lbl>Nombre del contacto</Lbl>
                  <Inp value={form.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} placeholder="Nombre del contacto" />
                </FormGrp>
                <FormGrp>
                  <Lbl>Teléfono</Lbl>
                  <Inp value={form.contacto_telefono} onChange={e => set('contacto_telefono', e.target.value)} placeholder="Teléfono de contacto" />
                </FormGrp>
              </Row2>
              <FormGrp>
                <Lbl>Correo electrónico</Lbl>
                <Inp type="email" value={form.contacto_correo} onChange={e => set('contacto_correo', e.target.value)} placeholder="correo@ejemplo.com" />
              </FormGrp>
              <FormGrp>
                <Lbl>Observaciones</Lbl>
                <Textarea value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Notas adicionales..." />
              </FormGrp>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
              <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {confirmId && (
        <Overlay onClick={buildOnBackdropClick(() => setConfirmId(null))} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="380px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Confirmar eliminación</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody><p>¿Eliminar este transportista? Esta acción no se puede deshacer.</p></ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => eliminar(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {showImportModal && (
        <ImportarModal
          entidad="transportistas"
          onClose={() => setShowImportModal(false)}
          onImportado={cargar}
        />
      )}
    </Page>
  );
}
